/**
 * localStorage utilities for persisting per-session request state.
 * This enables recovery from mobile app backgrounding and maintains
 * loading state across session switches.
 */

const STORAGE_KEY_PREFIX = "chatpod:session:";

/**
 * Possible states for a session's current request
 */
export type SessionRequestState =
  | { status: "idle" }
  | {
      status: "thinking";
      question: string;
      messageId: string;
      startedAt: number;
      messageCount: number; // Number of messages when request started (for recovery comparison)
    }
  | {
      status: "error";
      error: string;
      question: string;
      messageId: string;
    };

function getStorageKey(sessionId: string): string {
  return `${STORAGE_KEY_PREFIX}${sessionId}:request`;
}

/**
 * Save request state to localStorage for a session
 */
export function saveSessionRequestState(
  sessionId: string,
  state: SessionRequestState,
): void {
  try {
    if (state.status === "idle") {
      // Remove the entry when idle to keep localStorage clean
      localStorage.removeItem(getStorageKey(sessionId));
    } else {
      localStorage.setItem(getStorageKey(sessionId), JSON.stringify(state));
    }
  } catch {
    // localStorage may be full or disabled - fail silently
    console.warn("Failed to save session request state to localStorage");
  }
}

/**
 * Load request state from localStorage for a session
 */
export function loadSessionRequestState(
  sessionId: string,
): SessionRequestState | null {
  try {
    const stored = localStorage.getItem(getStorageKey(sessionId));
    if (!stored) return null;

    const parsed = JSON.parse(stored) as SessionRequestState;

    // Validate the shape
    if (
      parsed &&
      typeof parsed === "object" &&
      "status" in parsed &&
      (parsed.status === "thinking" || parsed.status === "error")
    ) {
      return parsed;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Clear request state for a session (set to idle)
 */
export function clearSessionRequestState(sessionId: string): void {
  try {
    localStorage.removeItem(getStorageKey(sessionId));
  } catch {
    // Fail silently
  }
}

/**
 * Get all session IDs that have pending (thinking) requests.
 * Useful for showing indicators in the sidebar.
 */
export function getPendingSessionIds(): string[] {
  const pendingIds: string[] = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_KEY_PREFIX) && key.endsWith(":request")) {
        const sessionId = key
          .slice(STORAGE_KEY_PREFIX.length)
          .replace(":request", "");
        const state = loadSessionRequestState(sessionId);
        if (state?.status === "thinking") {
          pendingIds.push(sessionId);
        }
      }
    }
  } catch {
    // Fail silently
  }

  return pendingIds;
}

/**
 * Clear stale requests that are older than maxAgeMs.
 * Called on app initialization to clean up orphaned entries.
 * Note: We still check the server for these before fully clearing.
 */
export function getStaleRequestSessionIds(maxAgeMs: number): string[] {
  const staleIds: string[] = [];
  const now = Date.now();

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_KEY_PREFIX) && key.endsWith(":request")) {
        const sessionId = key
          .slice(STORAGE_KEY_PREFIX.length)
          .replace(":request", "");
        const state = loadSessionRequestState(sessionId);

        if (state?.status === "thinking" && now - state.startedAt > maxAgeMs) {
          staleIds.push(sessionId);
        }
      }
    }
  } catch {
    // Fail silently
  }

  return staleIds;
}

/**
 * Clear all session request states (useful for logout)
 */
export function clearAllSessionRequestStates(): void {
  try {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_KEY_PREFIX) && key.endsWith(":request")) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Fail silently
  }
}
